import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material'
import { useNotify, useRefresh } from 'react-admin'
import { supabase } from '../supabaseClient'

interface Props {
  open: boolean
  onClose: () => void
  propertyId: string
  title: string
  currentFloor: number | null
}

const MIN_FLOOR = -5
const MAX_FLOOR = 100

/** Etykieta identyczna z widgetem: 0 = parter, brak = "—". */
const floorLabel = (floor: number | null) => {
  if (floor === null) return 'brak'
  if (floor === 0) return 'Parter'
  return `${floor} piętro`
}

export const EditFloorModal: React.FC<Props> = ({ open, onClose, propertyId, title, currentFloor }) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const [value, setValue] = useState(currentFloor === null || currentFloor === undefined ? '' : String(currentFloor))
  const [saving, setSaving] = useState(false)

  const raw = value.trim()
  // Puste pole = wyczyszczenie piętra (lokale bez piętra, np. komórki/garaże).
  const isCleared = raw === ''
  const parsed = Number(raw)
  const valid =
    isCleared ||
    (/^-?\d+$/.test(raw) && parsed >= MIN_FLOOR && parsed <= MAX_FLOOR)
  const newFloor = isCleared ? null : parsed
  const changed = valid && newFloor !== (currentFloor ?? null)

  async function confirm() {
    setSaving(true)
    const { data, error } = await supabase.rpc('update_property_floor_number', {
      p_property_id: propertyId,
      // NULL = wyczyszczenie piętra. Wygenerowane typy Supabase nie modelują
      // nullowalnych argumentów RPC, stąd rzutowanie.
      p_new_floor: newFloor as number,
    })
    setSaving(false)
    const result = data as { success?: boolean; error?: string } | null
    if (error || result?.success === false) {
      notify(error?.message || result?.error || 'Błąd zapisu piętra', { type: 'error' })
      return
    }
    notify('Zaktualizowano piętro', { type: 'success' })
    refresh()
    onClose()
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edycja piętra — {title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Nowe piętro"
          fullWidth
          margin="dense"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={!!value && !valid}
          helperText={
            !!value && !valid
              ? `Podaj liczbę całkowitą z zakresu ${MIN_FLOOR}–${MAX_FLOOR}`
              : '0 = parter. Puste pole = lokal bez piętra.'
          }
          inputProps={{ inputMode: 'numeric', 'data-testid': 'floor-input' }}
        />

        {/* Confirm — wpływ na widget przed akceptacją */}
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Piętro jest widoczne dla klientów w widgecie i steruje filtrem pięter.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Piętro:</Typography>
            <Typography variant="body2">
              {floorLabel(currentFloor ?? null)} →{' '}
              <strong>{valid ? floorLabel(newFloor) : '—'}</strong>
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Zakres suwaka „Piętro" w widgecie liczony jest z lokali inwestycji — zmiana może
            rozszerzyć lub zawęzić ten zakres dla całej inwestycji.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Nie dotyczy domów/segmentów opisanych polem „kondygnacje" (floor_levels).
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Anuluj</Button>
        <Button variant="contained" onClick={confirm} disabled={!changed || saving}>
          {saving ? 'Zapisywanie…' : 'Potwierdź zmianę'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
